import * as THREE from 'three';
import { type V2, dist } from '../core/math';
import { npcDef } from '../content/npcs';
import { ActorView } from '../render/ActorView';
import type { AssetLibrary } from '../render/assets';
import { Lighting, lookForZone } from '../render/Lighting';
import { PropField } from '../render/Props';
import { Terrain } from '../render/Terrain';
import { VfxDirector, type VfxHost } from '../render/vfx/VfxDirector';
import { Water } from '../render/Water';
import { ZoneVisuals } from '../render/ZoneVisuals';
import { Actor } from '../sim/actor';
import type { SimEvent } from '../sim/events';
import { createMonster } from '../sim/factory';
import type { NavGrid } from '../sim/navGrid';
import { LOCAL_ID_BASE } from '../sim/ids';
import type { EntityId, PeerId } from '../sim/types';
import { World } from '../sim/world';
import { buildNav } from '../zones/build';
import type { InteractableDef, MonsterPack, PortalDef, ZoneLayout } from '../zones/types';

export { LOCAL_ID_BASE };

/**
 * Per-zone scripted behaviour (rift dungeons, boss encounters, arenas).
 *
 * In multiplayer every peer runs the zone's scripts; the host (`world.isHost`)
 * is authoritative and mirrors its state to the others through `z.net`.
 */
export interface ZoneScript {
  start?(z: ZoneRuntime): void;
  tick?(z: ZoneRuntime, dt: number): void;
  event?(z: ZoneRuntime, ev: SimEvent): void;
  /** Called before each rendered frame (presentation-only work). */
  prerender?(z: ZoneRuntime, dt: number): void;
  /** A script message from another peer in this zone (sent with `z.net.send`). */
  message?(z: ZoneRuntime, from: string, data: Record<string, unknown>): void;
  /** A peer entered the zone: the host can push its script state to it. */
  peerJoined?(z: ZoneRuntime, peer: string): void;
  /** The zone host changed (the old host left or an older host was found). */
  hostChanged?(z: ZoneRuntime, host: string): void;
  dispose?(z: ZoneRuntime): void;
}

/** Networking seen from zone scripts (null while playing offline). */
export interface ZoneNet {
  /** Sends a script message to one peer, or to everyone in the zone. */
  send(data: Record<string, unknown>, to?: string): void;
  /** Other peers currently in the zone. */
  peers(): string[];
}

interface PackState {
  def: MonsterPack;
  members: EntityId[];
  respawnAt: number;
}

export interface ZoneRuntimeOptions {
  layout: ZoneLayout;
  assets: AssetLibrary;
  localPeer: PeerId;
  hostPeer: PeerId;
  partySize: number;
  seed: string;
  pvp?: boolean;
  /** Scale monster levels (instances use the party's level). */
  levelOverride?: number;
  /** No presentation (tests, simulations): skips VFX. */
  headless?: boolean;
}

/** Stand-in for the VFX director when running headless. */
const NULL_VFX: Pick<VfxDirector, 'handle' | 'update' | 'clear'> = { handle: () => {}, update: () => {}, clear: () => {} };

/**
 * One loaded zone instance: owns the three.js scene content for the zone,
 * the simulation World and the mapping between them.
 */
export class ZoneRuntime implements VfxHost {
  readonly layout: ZoneLayout;
  readonly scene = new THREE.Scene();
  readonly world: World;
  readonly nav: NavGrid;
  readonly lighting: Lighting;
  readonly vfx: Pick<VfxDirector, 'handle' | 'update' | 'clear'>;
  readonly views = new Map<EntityId, ActorView>();
  readonly packs: PackState[] = [];
  terrain: Terrain | null = null;
  water: Water | null = null;
  props: PropField | null = null;
  scripts: ZoneScript[] = [];
  /** Set by the network layer while this zone is shared with other peers. */
  net: ZoneNet | null = null;
  localPlayerId: EntityId = -1;
  private pendingViews = new Set<EntityId>();
  private localIds = LOCAL_ID_BASE;
  private shakeFn: (a: number) => void = () => {};
  private flashFn: (c: number, p: number, d: number) => void = () => {};
  private hitStopFn: (s: number) => void = () => {};
  /** Interactables still present (gathered/collected ones are removed). */
  interactables: InteractableDef[];
  readonly interactObjects = new Map<string, THREE.Object3D>();
  /**
   * Opens portals that carry a `requires` flag (e.g. `(p) => !!prog.char.flags[p.requires!]`).
   * While null, such portals render sealed; portals without `requires` are always open.
   */
  portalOpen: ((p: PortalDef) => boolean) | null = null;
  /** Presentation beyond terrain and props (markers, portals, lights, blob shadows, ambient); null until `buildScene`. */
  visuals: ZoneVisuals | null = null;
  private time = 0;

  constructor(readonly opts: ZoneRuntimeOptions) {
    this.layout = opts.layout;
    this.nav = buildNav(opts.layout);
    this.world = new World({ seed: opts.seed, nav: this.nav, localPeer: opts.localPeer, hostPeer: opts.hostPeer, partySize: opts.partySize, pvp: opts.pvp });
    this.lighting = new Lighting(this.scene);
    this.lighting.applyBiome(lookForZone(opts.layout));
    this.vfx = opts.headless ? NULL_VFX : new VfxDirector(this);
    this.interactables = [...opts.layout.interactables];
  }

  bindFeedback(shake: (a: number) => void, flash: (c: number, p: number, d: number) => void, hitStop: (s: number) => void): void {
    this.shakeFn = shake;
    this.flashFn = flash;
    this.hitStopFn = hitStop;
  }

  heightAt = (x: number, z: number): number => this.layout.heightAt(x, z);

  /** Builds terrain, water and props. */
  async buildScene(): Promise<void> {
    const L = this.layout;
    this.terrain = new Terrain({ minX: L.minX, minZ: L.minZ, width: L.width, depth: L.depth, heightAt: L.heightAt, surfaceAt: L.surfaceAt, ground: L.ground, scale: L.groundScale });
    this.scene.add(this.terrain.group);
    const sheet = L.water.find((w) => w.shape.kind === 'sheet');
    if (sheet) {
      this.water = new Water(L.minX, L.minZ, L.width, L.depth, sheet.level, L.heightAt, L.kind === 'sea' ? { deep: sheet.color, open: true } : {});
      this.scene.add(this.water.mesh);
    }
    this.props = new PropField();
    await this.props.build(L.props, this.opts.assets, L.heightAt);
    this.scene.add(this.props.group);
    this.visuals = new ZoneVisuals(this, this.props, this.opts.assets, this.opts.headless ? null : (this.vfx as VfxDirector));
    this.buildInteractableMarkers();
  }

  private buildInteractableMarkers(): void {
    for (const it of this.interactables) this.addInteractMarker(it);
  }

  /** Shows an interactable's world marker (node model, glint, ring, light). */
  addInteractMarker(it: InteractableDef): void {
    const g = this.visuals?.addMarker(it);
    if (g) this.interactObjects.set(it.id, g);
  }

  removeInteractable(id: string): void {
    this.interactables = this.interactables.filter((i) => i.id !== id);
    this.visuals?.removeMarker(id);
    this.interactObjects.delete(id);
  }

  allocLocalId(): EntityId {
    return this.localIds++;
  }

  /** Spawns NPCs locally (they are never replicated). */
  spawnNpcs(): void {
    for (const n of this.layout.npcs) {
      const def = npcDef(n.npc);
      const a = new Actor({ id: this.allocLocalId(), kind: 'npc', owner: this.world.localPeer, team: 'neutral', name: def.name, pos: { x: n.x, z: n.z }, facing: n.facing ?? 0, radius: 0.45 });
      a.npcId = def.id;
      a.look = { model: def.model };
      this.world.addActor(a);
      this.nav.blockCircle({ x: n.x, z: n.z }, 0.6);
    }
  }

  /** Host only: spawns every monster pack of the layout. */
  spawnPacks(): void {
    if (this.packs.length === 0) for (const def of this.layout.packs) this.packs.push({ def, members: [], respawnAt: 0 });
    if (!this.world.isHost) return;
    for (const st of this.packs) this.spawnPack(st);
  }

  /** A peer became host: adopt the old host's monsters and rebuild pack bookkeeping. */
  adoptMonsters(fromPeer: string): void {
    const me = this.world.localPeer;
    for (const a of this.world.actors.values()) if (a.kind === 'monster' && a.owner === fromPeer) a.owner = me;
    if (this.packs.length === 0) for (const def of this.layout.packs) this.packs.push({ def, members: [], respawnAt: 0 });
    for (const st of this.packs) {
      st.members = [...this.world.actors.values()].filter((a) => a.monster?.tags.includes(`pack:${st.def.id}`)).map((a) => a.id);
      st.respawnAt = 0;
    }
  }

  /** Removes every monster this peer owns (yielding to an older host). */
  dropOwnedMonsters(): void {
    for (const a of [...this.world.actors.values()]) if (a.kind === 'monster' && this.world.isLocal(a)) this.world.removeActor(a.id);
    for (const st of this.packs) st.members = [];
  }

  private spawnPack(st: PackState): void {
    const d = st.def;
    const level = this.opts.levelOverride ?? d.level;
    let i = 0;
    const total = d.monsters.reduce((s, m) => s + m.count, 0);
    for (const m of d.monsters) {
      for (let k = 0; k < m.count; k++) {
        const ang = (i / Math.max(1, total)) * Math.PI * 2 + this.world.rng.range(-0.3, 0.3);
        const r = total === 1 ? 0 : d.radius * this.world.rng.range(0.4, 1);
        let p: V2 = { x: d.x + Math.sin(ang) * r, z: d.z + Math.cos(ang) * r };
        if (!this.nav.walkable(p)) p = this.nav.nearestWalkable(p) ?? { x: d.x, z: d.z };
        const a = createMonster(this.world, m.def, level, p, { facing: this.world.rng.range(-Math.PI, Math.PI), tags: [...(d.tags ?? []), `pack:${d.id}`] });
        st.members.push(a.id);
        i++;
      }
    }
  }

  spawnMonster(def: string, level: number, p: V2, opts: Parameters<typeof createMonster>[4] = {}): Actor {
    return createMonster(this.world, def, level, p, opts);
  }

  // ---------------------------------------------------------------- views

  /**
   * Builds the actor's view, or rebuilds it when `actor.look.model` changed
   * (transformations). The old view stays visible until the new model loads.
   */
  private ensureView(a: Actor): void {
    if (this.views.get(a.id)?.modelId === a.look.model || this.pendingViews.has(a.id)) return;
    this.pendingViews.add(a.id);
    const model = a.look.model;
    void this.opts.assets.load(model).then((t) => {
      this.pendingViews.delete(a.id);
      const old = this.views.get(a.id);
      // Gone, already built, or the look changed again while loading (the next frame retries).
      if (!this.world.actors.has(a.id) || old?.modelId === model || a.look.model !== model) return;
      const v = new ActorView(a, t, this.opts.assets, model, this.visuals?.viewOptions(a));
      this.visuals?.decorate(v, a);
      if (old) {
        this.scene.remove(old.root);
        old.dispose();
      }
      this.views.set(a.id, v);
      this.scene.add(v.root);
    });
  }

  private removeView(id: EntityId): void {
    const v = this.views.get(id);
    if (!v) return;
    this.scene.remove(v.root);
    v.dispose();
    this.views.delete(id);
  }

  // ---------------------------------------------------------------- VfxHost

  actorPos(id: number, out: THREE.Vector3): THREE.Vector3 | null {
    const v = this.views.get(id);
    if (v) return out.copy(v.root.position);
    const a = this.world.actors.get(id);
    if (!a) return null;
    return out.set(a.pos.x, this.heightAt(a.pos.x, a.pos.z), a.pos.z);
  }
  actorHeight(id: number): number {
    return this.views.get(id)?.headHeight ?? 1.8;
  }
  hitFlash(id: number): void {
    this.views.get(id)?.hitFlash();
  }
  shake(amount: number): void {
    this.shakeFn(amount);
  }
  screenFlash(color: number, peak: number, duration: number): void {
    this.flashFn(color, peak, duration);
  }
  isLocalPlayer(id: number): boolean {
    return id === this.localPlayerId;
  }
  hitStop(seconds: number): void {
    this.hitStopFn(seconds);
  }
  projectilePos(id: number): { x: number; z: number } | null {
    for (const p of this.world.projectiles) if (p.id === id) return p.pos;
    return null;
  }

  // ---------------------------------------------------------------- update

  /** Fixed-step simulation tick. Returns the events produced. */
  step(dt: number): SimEvent[] {
    this.time += dt;
    this.world.update(dt);
    for (const s of this.scripts) s.tick?.(this, dt);
    this.updatePacks();
    const events = this.world.drainEvents();
    for (const ev of events) this.onEvent(ev);
    for (const s of this.scripts) for (const ev of events) s.event?.(this, ev);
    return events;
  }

  private updatePacks(): void {
    if (!this.world.isHost) return;
    for (const st of this.packs) {
      if (st.def.respawn === undefined) continue;
      const alive = st.members.some((id) => this.world.actors.get(id)?.alive);
      if (alive) continue;
      if (st.respawnAt === 0) st.respawnAt = this.world.time + st.def.respawn;
      else if (this.world.time >= st.respawnAt) {
        // Don't pop monsters on top of a player.
        const near = this.world.aliveActors.some((a) => a.kind === 'player' && dist(a.pos, st.def) < st.def.radius + 12);
        if (near) {
          st.respawnAt = this.world.time + 5;
          continue;
        }
        for (const id of st.members) this.world.removeActor(id);
        st.members = [];
        st.respawnAt = 0;
        this.spawnPack(st);
      }
    }
    // Clean up corpses.
    for (const a of this.world.actors.values()) {
      if (a.kind === 'monster' && !a.alive && this.world.time - a.diedAt > 6 && this.world.isLocal(a)) {
        const inPack = this.packs.some((p) => p.members.includes(a.id));
        if (!inPack) this.world.removeActor(a.id);
      }
    }
  }

  private onEvent(ev: SimEvent): void {
    switch (ev.type) {
      case 'spawn': {
        const a = this.world.actors.get(ev.id);
        if (a) this.ensureView(a);
        break;
      }
      case 'despawn':
        this.removeView(ev.id);
        break;
      case 'castStage': {
        const v = this.views.get(ev.actorId);
        if (v && ev.phase === 'stage') v.playStage(`${ev.castId}:${ev.stage}`, ev.anim, ev.duration, ev.anim === 'Spin' || ev.anim === 'Channel');
        break;
      }
      case 'castEnd':
        this.views.get(ev.actorId)?.endStage();
        break;
      case 'reaction':
        if (ev.kind === 'flinch' || ev.kind === 'knockback' || ev.kind === 'pull') this.views.get(ev.tgtId)?.flinch();
        break;
      default:
        break;
    }
    this.vfx.handle(ev);
  }

  /** Per-frame presentation update (after simulation steps). */
  render(dt: number, alpha: number): void {
    for (const s of this.scripts) s.prerender?.(this, dt);
    for (const a of this.world.actors.values()) {
      const v = this.views.get(a.id);
      // Missing view, or the actor's model changed (e.g. a transformation): (re)build it.
      if (!v || v.modelId !== a.look.model) this.ensureView(a);
    }
    // Glows (counter, stagger, identity modes), weapons, broken parts, culling, blob shadows, portals, lights.
    if (this.visuals) this.visuals.frame(dt, alpha, this.world.actors.values(), this.views, this.world.time);
    else for (const a of this.world.actors.values()) this.views.get(a.id)?.update(dt, a, alpha, this.heightAt);
    this.water?.update(dt);
    this.vfx.update(dt);
  }

  portalAt(p: V2): PortalDef | null {
    for (const portal of this.layout.portals) if (dist(p, portal) <= portal.radius) return portal;
    return null;
  }

  nearestInteractable(p: V2, range = 2.8): InteractableDef | null {
    let best: InteractableDef | null = null;
    let bestD = range;
    for (const it of this.interactables) {
      const d = dist(p, it);
      if (d < bestD) {
        best = it;
        bestD = d;
      }
    }
    return best;
  }

  nearestNpc(p: V2, range = 3.2): Actor | null {
    let best: Actor | null = null;
    let bestD = range;
    for (const a of this.world.actors.values()) {
      if (a.kind !== 'npc') continue;
      const d = dist(p, a.pos);
      if (d < bestD) {
        best = a;
        bestD = d;
      }
    }
    return best;
  }

  dispose(): void {
    for (const s of this.scripts) s.dispose?.(this);
    for (const id of [...this.views.keys()]) this.removeView(id);
    this.vfx.clear();
    this.visuals?.dispose();
    this.terrain?.dispose();
    this.water?.dispose();
    this.props?.dispose();
    this.scene.clear();
  }
}

